import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Download, Eye, File, FileText, Loader2, Paperclip } from "lucide-react";
import moment from "moment";
import { toast } from "../../utils/toast";
import translations from "./Translations";
import AttachmentPreviewModal from "./AttachmentPreviewModal";
import ModalCloseButton from "./ModalCloseButton";
import { attachmentKey, attachmentKind, downloadFile } from "../../utils/attachments";

const SOURCE_LABEL_KEYS = {
  RC: "attachmentSourceRc",
  TASK: "attachmentSourceTask",
  INSTALMENT_PLAN: "attachmentSourceInstalment",
};
const SOURCE_FALLBACK = { RC: "RC", TASK: "Task", INSTALMENT_PLAN: "Instalment Plan" };

const formatUploadedAt = (value) => (value ? moment(value).format("DD MMM YYYY, HH:mm") : "-");

// Image attachments show the picture itself; PDFs and other files an icon.
// A thumbnail that fails to load falls back to the icon.
const AttachmentThumb = ({ attachment, size, isDark, onClick, className = "" }) => {
  const [broken, setBroken] = useState(false);
  const kind = attachmentKind(attachment.fileName, attachment.viewUrl);
  const showImage = kind === "image" && attachment.viewUrl && !broken;
  const Icon = kind === "pdf" ? FileText : File;
  const Tag = onClick ? "button" : "span";
  return (
    <Tag
      {...(onClick ? { type: "button", onClick, "aria-label": attachment.fileName } : {})}
      style={{ width: size, height: size }}
      className={`flex shrink-0 items-center justify-center overflow-hidden rounded-md border ${
        onClick ? "transition-opacity hover:opacity-80" : ""
      } ${isDark ? "border-gray-700 bg-gray-800" : "border-slate-200 bg-white"} ${className}`}
    >
      {showImage ? (
        <img
          src={attachment.viewUrl}
          alt=""
          loading="lazy"
          onError={() => setBroken(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        <Icon size={size / 2} className={kind === "pdf" ? "text-red-500" : "text-gray-400"} />
      )}
    </Tag>
  );
};

/**
 * Table-cell form: one button showing a few stacked thumbnails and the
 * count, which opens a dialog with the full list. Keeps a row one line tall
 * however many files it has.
 */
const AttachmentSummary = ({ list, resolveFresh, theme, langMode }) => {
  const [open, setOpen] = useState(false);
  const isDark = theme === "dark";
  const label = translations[langMode]?.attachments ?? "Attachments";
  const shown = list.slice(0, 3);

  useEffect(() => {
    if (!open) return undefined;
    // The preview opened from inside the dialog handles its own Esc first
    // (capture phase) and stops it, so this only closes the dialog.
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={label}
        className={`inline-flex items-center gap-2 rounded-full border py-1 pe-3 ps-1 text-xs font-semibold transition-colors ${
          isDark
            ? "border-gray-700 bg-gray-800 text-sky-400 hover:bg-gray-700"
            : theme === "high-contrast"
              ? "high-contrast border"
              : "border-slate-200 bg-white text-[#236DB4] hover:bg-blue-50"
        }`}
      >
        <span className="flex -space-x-2 rtl:space-x-reverse">
          {shown.map((attachment) => (
            <AttachmentThumb
              key={attachmentKey(attachment)}
              attachment={attachment}
              size={24}
              isDark={isDark}
              className="rounded-full ring-2 ring-white dark:ring-gray-800"
            />
          ))}
        </span>
        <Paperclip size={12} className="shrink-0" />
        {list.length}
      </button>

      {open &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-label={label}
            onClick={() => setOpen(false)}
          >
            <div
              className={`flex max-h-[85vh] w-[min(720px,92vw)] flex-col overflow-hidden rounded-xl shadow-2xl ${
                isDark ? "bg-gray-900 text-gray-100" : "bg-white text-slate-800"
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className={`flex items-center justify-between gap-3 border-b px-4 py-3 ${
                  isDark ? "border-gray-700" : "border-slate-200"
                }`}
              >
                <h3 className="flex items-center gap-2 text-sm font-semibold sm:text-base">
                  <Paperclip size={15} className="shrink-0 opacity-70" />
                  {label}
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                      isDark ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {list.length}
                  </span>
                </h3>
                <ModalCloseButton onClick={() => setOpen(false)} ariaLabel="Close attachments" />
              </div>
              <div className="overflow-y-auto p-4">
                <AttachmentList
                  attachments={list}
                  resolveFresh={resolveFresh}
                  theme={theme}
                  langMode={langMode}
                  iconOnly
                />
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

/**
 * Renders history attachments ({ fileName, source, uploadedAt, viewUrl,
 * downloadUrl }) as cards with View / Download actions, or, with `compact`,
 * as a single summary button for table cells. Images and PDFs are viewed in
 * a modal; downloads save in the current tab.
 *
 * downloadUrl is presigned and expires after ~60 minutes, so the list never
 * uses the one it was rendered with: `resolveFresh(attachment)` must refetch
 * the history call and return the matching attachment (or null). Its
 * downloadUrl is used, falling back to viewUrl when the backend sent none.
 */
const AttachmentList = ({ attachments, resolveFresh, theme, langMode, compact = false, iconOnly = false }) => {
  const [pendingKey, setPendingKey] = useState(null);
  const [preview, setPreview] = useState(null);
  const list = Array.isArray(attachments) ? attachments : [];
  if (list.length === 0) return null;

  if (compact) {
    return <AttachmentSummary list={list} resolveFresh={resolveFresh} theme={theme} langMode={langMode} />;
  }

  const unavailable = () =>
    toast.error(translations[langMode]?.attachmentUnavailable ?? "Attachment is not available");

  const handleView = (attachment) => {
    if (!attachment?.viewUrl) {
      unavailable();
      return;
    }
    const kind = attachmentKind(attachment.fileName, attachment.viewUrl);
    if (kind === "file") {
      // Not previewable in the page; let the browser handle it.
      window.open(attachment.viewUrl, "_blank", "noopener,noreferrer");
      return;
    }
    setPreview({ attachment, kind });
  };

  const handleDownload = async (attachment) => {
    const key = attachmentKey(attachment);
    if (pendingKey === key) return;
    setPendingKey(key);
    try {
      let fresh = attachment;
      try {
        fresh = (resolveFresh ? await resolveFresh(attachment) : attachment) || attachment;
      } catch (error) {
        // Refetch failed: the view link still gets the user the file.
        console.error("Error refreshing attachment link:", error);
        fresh = { ...attachment, downloadUrl: null };
      }
      const url = fresh.downloadUrl || fresh.viewUrl || attachment.viewUrl;
      if (!url) {
        unavailable();
        return;
      }
      await downloadFile(url, attachment.fileName, { isAttachmentUrl: Boolean(fresh.downloadUrl) });
    } finally {
      setPendingKey(null);
    }
  };

  const isDark = theme === "dark";
  const isHc = theme === "high-contrast";
  const viewLabel = translations[langMode]?.viewLabel ?? "View";
  const downloadLabel = translations[langMode]?.download ?? "Download";
  // iconOnly (the attachments dialog): square icon buttons, label as tooltip.
  const btnClass = `inline-flex items-center justify-center gap-1 rounded-md text-xs font-semibold transition-colors ${
    iconOnly ? "h-8 w-8" : "px-2 py-1"
  } ${
    isDark
      ? "bg-sky-500/10 text-sky-400 hover:bg-sky-500/20"
      : isHc
        ? "high-contrast border"
        : "bg-blue-50 text-[#236DB4] hover:bg-blue-100"
  }`;

  return (
    <>
      <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {list.map((attachment) => {
          const key = attachmentKey(attachment);
          const sourceLabel =
            translations[langMode]?.[SOURCE_LABEL_KEYS[attachment.source]] ??
            SOURCE_FALLBACK[attachment.source] ??
            attachment.source;
          return (
            <li
              key={key}
              className={`flex items-center gap-3 rounded-lg border p-2 ${
                isDark ? "border-gray-700 bg-gray-900/40" : isHc ? "high-contrast border-2" : "border-slate-200 bg-slate-50/60"
              }`}
            >
              <AttachmentThumb
                attachment={attachment}
                size={48}
                isDark={isDark}
                onClick={() => handleView(attachment)}
              />
              <div className="min-w-0 flex-1 text-left rtl:text-right">
                <p
                  className={`truncate text-xs font-medium sm:text-sm ${isDark ? "text-gray-100" : "text-slate-700"}`}
                  title={attachment.fileName}
                >
                  {attachment.fileName || "-"}
                </p>
                <p className={`mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] ${isDark ? "text-gray-400" : "text-slate-500"}`}>
                  {sourceLabel && (
                    <span className={`rounded px-1.5 py-px font-semibold ${isDark ? "bg-gray-700 text-gray-300" : "bg-slate-200 text-slate-600"}`}>
                      {sourceLabel}
                    </span>
                  )}
                  {formatUploadedAt(attachment.uploadedAt)}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => handleView(attachment)}
                  className={btnClass}
                  title={viewLabel}
                  aria-label={viewLabel}
                >
                  <Eye size={iconOnly ? 15 : 12} />
                  {!iconOnly && viewLabel}
                </button>
                <button
                  type="button"
                  onClick={() => handleDownload(attachment)}
                  disabled={pendingKey === key}
                  className={`${btnClass} disabled:cursor-wait disabled:opacity-60`}
                  title={downloadLabel}
                  aria-label={downloadLabel}
                >
                  {pendingKey === key ? (
                    <Loader2 size={iconOnly ? 15 : 12} className="animate-spin" />
                  ) : (
                    <Download size={iconOnly ? 15 : 12} />
                  )}
                  {!iconOnly && downloadLabel}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
      {preview && (
        <AttachmentPreviewModal
          url={preview.attachment.viewUrl}
          fileName={preview.attachment.fileName}
          kind={preview.kind}
          onClose={() => setPreview(null)}
          onDownload={() => handleDownload(preview.attachment)}
          downloading={pendingKey === attachmentKey(preview.attachment)}
          langMode={langMode}
        />
      )}
    </>
  );
};

export default AttachmentList;
